/**
 * Decide which menu category a deep-link route (e.g. `/liquor`, #514) should
 * land on.
 *
 * Pure and dependency-free, like components/admin/categorySelection.js, so the
 * matching rules can be unit-tested without mounting Menu and its four
 * contexts.
 *
 * Why this is fuzzier than a lookup table: Clover categories have no slug and
 * no stable human key. All we get is an opaque id and a display name that
 * netlify/functions/translate-menu.js rewrites per language. In production
 * today the same category is "Liquor" in English and "Espíritu" in Spanish —
 * a machine translation nobody chose, which a re-translation can change again.
 * So we match on id when we have one and fall back to a normalized name match,
 * and we always tolerate finding nothing.
 */

/**
 * Normalize a category display name — or a URL token — into a comparable slug.
 *
 * The NFD + combining-mark strip is what lets "Espíritu" and "Espiritu" collapse
 * together, which matters because the translated names are not ours to control
 * and arrive with whatever accents the translator emitted.
 *
 * @param {string} name
 * @returns {string} a lowercase hyphenated slug, or '' for unusable input
 */
export const slugifyCategoryName = (name) =>
  String(name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * URL token -> the category names that should satisfy it, in priority order.
 *
 * Every alias here is deliberate, and the omissions matter as much as the
 * entries. "bebidas" is NOT an alias for liquor: production already uses it as
 * the Spanish name of "Mocktails and Beverages", so it would hijack the link to
 * the non-alcoholic menu. Likewise "cerveza" and "cocteles" are live sibling
 * categories (Beer, Cocktails), not synonyms.
 *
 * "espiritu" is here because it is what production actually serves in Spanish
 * today; "licor"/"licores" are here because that is what a corrected
 * re-translation would most likely produce.
 */
export const CATEGORY_SLUG_ALIASES = {
  liquor: [
    'liquor',
    'liquors',
    'licor',
    'licores',
    'espiritu',
    'espiritus',
    'spirits',
    'alcohol',
  ],
};

/**
 * Resolve a human-readable slug to a live category id.
 *
 * `categories` MUST already be filtered to what the site is willing to show
 * (Menu.jsx applies its showOnWeb filter first), so a category hidden in Clover
 * is simply unresolvable here and needs no special case.
 *
 * @param {Array<{id: string, name?: string}>} categories - visible categories
 * @param {string} slug - the URL token, e.g. 'liquor'
 * @returns {string|null} the category id, or null when nothing matches
 */
export const resolveCategoryIdFromSlug = (categories, slug) => {
  if (!Array.isArray(categories)) return null;

  const target = slugifyCategoryName(slug);
  if (!target) return null;

  const candidates = [target, ...(CATEGORY_SLUG_ALIASES[target] || [])];

  const entries = categories
    .filter((category) => category?.id)
    .map((category) => ({
      id: category.id,
      slug: slugifyCategoryName(category.name),
    }));

  // Pass 1: exact name match, walking the aliases in priority order so the
  // canonical name wins over a synonym when the menu contains both.
  for (const candidate of candidates) {
    const hit = entries.find((entry) => entry.slug === candidate);
    if (hit) return hit.id;
  }

  // Pass 2: whole-token containment, so a decorated Clover name like
  // "BAR - Liquor" or "Liquor / Cocktails" still resolves. Token-level rather
  // than substring so "liquor" can never match "liquorice".
  for (const candidate of candidates) {
    const hit = entries.find((entry) =>
      entry.slug.split('-').includes(candidate),
    );
    if (hit) return hit.id;
  }

  return null;
};

/**
 * The single decision for "which category should Menu show right now".
 *
 * Precedence: the deep link, then the order the admin dragged into
 * siteInfo/categoryOrder, then natural order. The last two branches reproduce
 * the behaviour that previously lived inline in Menu.jsx.
 *
 * Returning null means "nothing to select" — the caller should leave state
 * alone rather than blanking the menu.
 *
 * @param {object} params
 * @param {Array<{id: string, name?: string}>} params.categories - visible categories
 * @param {Array<string>|null} [params.categoryOrder] - ids from siteInfo/categoryOrder
 * @param {string|null} [params.deepLinkCategoryId] - exact id hint from the route
 * @param {string|null} [params.deepLinkSlug] - name token from the route
 * @returns {string|null} the category id to select
 */
export const resolveInitialCategoryId = ({
  categories,
  categoryOrder = null,
  deepLinkCategoryId = null,
  deepLinkSlug = null,
}) => {
  if (!Array.isArray(categories) || categories.length === 0) return null;

  // The id is exact and survives renames and translation churn, but it is only
  // a hint: if Clover recreated the category it now has a different id, and the
  // slug match below is what recovers the link without a code change.
  if (
    deepLinkCategoryId &&
    categories.some((category) => category?.id === deepLinkCategoryId)
  ) {
    return deepLinkCategoryId;
  }

  if (deepLinkSlug) {
    const matched = resolveCategoryIdFromSlug(categories, deepLinkSlug);
    if (matched) return matched;
  }

  if (Array.isArray(categoryOrder) && categoryOrder.length > 0) {
    const firstValid = categoryOrder.find((id) =>
      categories.some((category) => category?.id === id),
    );
    if (firstValid) return firstValid;
  }

  return categories[0].id;
};
